// 第三方库
import { makeObservable, observable, action, runInAction } from 'mobx';

// 内部模块 - 工具
import { logger } from '../utils/logger';

// 内部模块 - Store
// 延迟导入以避免循环依赖

/**
 * 批量更新管理器
 * 负责收集和批量处理用户列表的更新操作，提升性能
 */
class BatchUpdateManager {
  constructor() {
    // 批量更新队列
    this._updateQueue = new Map(); // userId -> updateData
    this._isProcessing = false;
    this._batchTimeout = null;
    this._batchDelay = 200; // 200ms 批量延迟，与后端保持一致

    // 更新回调函数
    this._updateCallbacks = new Set();

    makeObservable(this, {
      _updateQueue: observable,
      _isProcessing: observable,
      addUpdate: action,
      processBatch: action,
      clearBatch: action,
    });
  }

  /**
   * 添加更新到批量队列
   * @param {string} userId - 用户ID
   * @param {Object} updateData - 更新数据
   */
  addUpdate = (userId, updateData) => {
    const normalizedUserId = String(userId);

    // 合并更新数据
    const existingUpdate = this._updateQueue.get(normalizedUserId) || {};
    const mergedUpdate = {
      ...existingUpdate,
      ...updateData,
      userId: normalizedUserId,
      timestamp: Date.now(),
    };

    this._updateQueue.set(normalizedUserId, mergedUpdate);

    logger.debug('[BatchUpdateManager] 添加更新到队列', {
      userId: normalizedUserId,
      updateData: mergedUpdate,
      queueSize: this._updateQueue.size,
    });

    // 启动批量处理定时器
    this._scheduleBatchProcess();
  };

  /**
   * 注册更新回调函数
   * @param {Function} callback - 回调函数
   */
  registerUpdateCallback = callback => {
    this._updateCallbacks.add(callback);
  };

  /**
   * 取消注册更新回调函数
   * @param {Function} callback - 回调函数
   */
  unregisterUpdateCallback = callback => {
    this._updateCallbacks.delete(callback);
  };

  /**
   * 安排批量处理
   */
  _scheduleBatchProcess = () => {
    if (this._batchTimeout) {
      clearTimeout(this._batchTimeout);
    }

    this._batchTimeout = setTimeout(() => {
      this.processBatch();
    }, this._batchDelay);
  };

  /**
   * 处理批量更新
   */
  processBatch = () => {
    if (this._isProcessing || this._updateQueue.size === 0) {
      return;
    }

    runInAction(() => {
      this._isProcessing = true;
    });

    try {
      const updates = Array.from(this._updateQueue.values());

      logger.info('[BatchUpdateManager] 开始处理批量更新', {
        updateCount: updates.length,
        updates: updates.map(u => ({
          userId: u.userId,
          hasUnreadCount: 'unreadCount' in u,
          hasMessage: 'message' in u,
        })),
      });

      // 按类型分组更新
      const unreadCountUpdates = [];
      const messageUpdates = [];
      const userMoves = [];

      updates.forEach(update => {
        if ('unreadCount' in update) {
          unreadCountUpdates.push(update);
        }
        if ('message' in update) {
          messageUpdates.push(update);
          // 有新消息的用户需要移动到顶部
          userMoves.push(update);
        }
      });

      // 批量处理未读消息更新
      if (unreadCountUpdates.length > 0) {
        this._processUnreadCountUpdates(unreadCountUpdates);
      }

      // 批量处理消息更新
      if (messageUpdates.length > 0) {
        this._processMessageUpdates(messageUpdates);
      }

      // 批量处理用户移动
      if (userMoves.length > 0) {
        this._processUserMoves(userMoves);
      }

      // 通知所有注册的回调函数
      this._notifyUpdateCallbacks(updates);

      logger.info('[BatchUpdateManager] 批量更新处理完成', {
        processedCount: updates.length,
      });
    } catch (error) {
      logger.error('[BatchUpdateManager] 批量更新处理失败:', error);
    } finally {
      runInAction(() => {
        this._isProcessing = false;
        this._updateQueue.clear();
      });
    }
  };

  /**
   * 处理未读消息更新
   * @param {Array} updates - 更新数组
   */
  _processUnreadCountUpdates = updates => {
    logger.debug('[BatchUpdateManager] 处理未读消息更新', {
      count: updates.length,
    });

    // 使用静态导入避免动态导入问题
    updates.forEach(update => {
      const userId = update.userId;
      const unreadCount = update.unreadCount;

      if (userId && unreadCount !== undefined) {
        // 通过 UserInfoUpdateStore 更新未读计数，确保界面显示正确
        import('./UserInfoUpdateStore')
          .then(module => {
            module.userInfoUpdateStore.updateUserUnreadCount(userId, unreadCount);

            logger.debug('[BatchUpdateManager] 通过UserInfoUpdateStore更新用户未读计数', {
              userId,
              unreadCount,
            });
          })
          .catch(error => {
            logger.error('[BatchUpdateManager] 动态导入UserInfoUpdateStore失败:', error);
          });
      }
    });
  };

  /**
   * 处理消息更新
   * @param {Array} updates - 更新数组
   */
  _processMessageUpdates = updates => {
    logger.debug('[BatchUpdateManager] 处理消息更新', {
      count: updates.length,
    });

    // 这里可以添加特定的消息更新逻辑
    // 例如：更新最新消息内容、时间戳等
  };

  /**
   * 处理用户移动
   * @param {Array} updates - 更新数组
   */
  _processUserMoves = updates => {
    logger.debug('[BatchUpdateManager] 处理用户移动', {
      count: updates.length,
      userIds: updates.map(u => u.userId),
    });

    // 这里可以添加特定的用户移动逻辑
    // 例如：将有新消息的用户移动到列表顶部
  };

  /**
   * 通知更新回调函数
   * @param {Array} updates - 更新数组
   */
  _notifyUpdateCallbacks = updates => {
    this._updateCallbacks.forEach(callback => {
      try {
        callback(updates);
      } catch (error) {
        logger.error('[BatchUpdateManager] 更新回调执行失败:', error);
      }
    });
  };

  /**
   * 清空批量队列
   */
  clearBatch = () => {
    if (this._batchTimeout) {
      clearTimeout(this._batchTimeout);
      this._batchTimeout = null;
    }

    runInAction(() => {
      this._updateQueue.clear();
      this._isProcessing = false;
    });

    logger.debug('[BatchUpdateManager] 批量队列已清空');
  };

  /**
   * 获取当前队列状态
   */
  getQueueStatus = () => {
    return {
      queueSize: this._updateQueue.size,
      isProcessing: this._isProcessing,
      hasTimeout: !!this._batchTimeout,
    };
  };

  /**
   * 强制处理当前队列
   */
  forceProcess = () => {
    if (this._batchTimeout) {
      clearTimeout(this._batchTimeout);
      this._batchTimeout = null;
    }
    this.processBatch();
  };
}

// 创建单例实例
export const batchUpdateManager = new BatchUpdateManager();
